/**
 * 同轴心错位配合 PropertyManager 状态策略。
 *
 * 文件职责：
 * 1. 按 SOLIDWORKS 的 misaligned concentric mate 语义解析可链接的同轴心 Mate；
 * 2. 为控制器提供可直接发布给 UI 的纯数据状态补丁；
 * 3. 避免 web-client 读取 Mate 文档后自行判断几何或约束关系。
 */

import {
  AssemblyMateType,
  MateConcentricMisalignmentType,
  type AssemblyMateDefinition,
} from '../../definition';
import {
  resolveMateCandidatePair,
  type AssemblyMateSelectionCandidate,
} from '../../selection';
import type {
  AssemblyMateConcentricMisalignmentLinkedMateOption,
  AssemblyMateControllerState,
} from '../AssemblyMateControllerTypes';

/** SOLIDWORKS 2018 官方博客说明的默认最大错位偏差，单位按当前模型长度单位处理。 */
export const DEFAULT_CONCENTRIC_MISALIGNMENT_MAXIMUM_DEVIATION = 12.7;

/**
 * 解析无方向组件对 key。
 *
 * @param firstComponentInstanceId 第一组件实例 ID。
 * @param secondComponentInstanceId 第二组件实例 ID。
 * @returns 可稳定比较的无方向组件对 key。
 */
function resolveComponentPairKey(
  firstComponentInstanceId: string,
  secondComponentInstanceId: string,
): string {
  return [
    firstComponentInstanceId,
    secondComponentInstanceId,
  ].sort().join('::');
}

/**
 * 解析候选实体对应的无方向组件对 key。
 *
 * @param candidates 当前 PropertyManager 中的两个配合候选。
 * @returns 组件对 key；候选不足时返回 null。
 */
function resolveCandidateComponentPairKey(
  candidates: readonly AssemblyMateSelectionCandidate[],
): string | null {
  const pair = resolveMateCandidatePair(candidates);
  if (!pair) {
    return null;
  }
  return resolveComponentPairKey(
    pair[0].entityRef.componentInstanceId,
    pair[1].entityRef.componentInstanceId,
  );
}

/**
 * 判断已提交 Mate 是否可作为当前同轴心错位配合的 linked mate。
 *
 * @param input 判断输入。
 * @returns 是否可链接。
 */
function canUseLinkedConcentricMate(input: {
  /** 当前候选所属的组件对 key。 */
  readonly candidateComponentPairKey: string;
  /** 正在检查的已提交 Mate。 */
  readonly mate: AssemblyMateDefinition;
  /** 当前草稿或编辑 Mate ID。 */
  readonly currentMateId: string | null;
}): boolean {
  if (input.mate.mateType !== AssemblyMateType.Concentric || input.mate.suppressed) {
    return false;
  }
  if (input.currentMateId && input.mate.mateId === input.currentMateId) {
    return false;
  }
  if (input.mate.concentricMisalignment) {
    /**
     * 当前求解器只支持一对同轴心 Mate 的错位关系。把已经错位的 Mate 再串入新关系会形成
     * 不清晰的偏差归属，SOLIDWORKS 也会把错位 Mate 成对组织在 Misaligned folder 中。
     */
    return false;
  }
  return resolveComponentPairKey(
    input.mate.firstEntity.componentInstanceId,
    input.mate.secondEntity.componentInstanceId,
  ) === input.candidateComponentPairKey;
}

/**
 * 创建 linked mate 选项标签。
 *
 * @param mate 已提交同轴心 Mate。
 * @returns PropertyManager 可展示的标签。
 */
function formatLinkedMateOptionLabel(mate: AssemblyMateDefinition): string {
  return `同轴心 (${mate.firstEntity.label}, ${mate.secondEntity.label})`;
}

/**
 * 解析当前候选可链接的已提交同轴心 Mate。
 *
 * @param input 解析输入。
 * @returns linked mate 选项列表。
 */
function resolveLinkedConcentricMateOptions(input: {
  /** 当前配合类型。 */
  readonly mateType: AssemblyMateType | null;
  /** 当前 PropertyManager 配合候选。 */
  readonly candidates: readonly AssemblyMateSelectionCandidate[];
  /** 当前 Mate 文档内的 Mate 列表。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 当前草稿或编辑 Mate ID。 */
  readonly currentMateId: string | null;
}): readonly AssemblyMateConcentricMisalignmentLinkedMateOption[] {
  if (input.mateType !== AssemblyMateType.Concentric) {
    return [];
  }

  const candidateComponentPairKey = resolveCandidateComponentPairKey(input.candidates);
  if (!candidateComponentPairKey) {
    return [];
  }

  return input.mateDefinitions
    .filter((mate) => canUseLinkedConcentricMate({
      candidateComponentPairKey,
      mate,
      currentMateId: input.currentMateId,
    }))
    .map((mate) => ({
      mateId: mate.mateId,
      label: formatLinkedMateOptionLabel(mate),
    }));
}

/**
 * 解析同轴心错位最大偏差。
 *
 * @param currentValue 当前状态中的最大偏差。
 * @returns 可用于 Mate Definition 的最大偏差。
 */
function resolveMaximumDeviation(currentValue: number): number {
  return Number.isFinite(currentValue) && currentValue >= 0
    ? currentValue
    : DEFAULT_CONCENTRIC_MISALIGNMENT_MAXIMUM_DEVIATION;
}

/**
 * 解析同轴心错位状态补丁。
 *
 * @param input 状态解析输入。
 * @returns 可合并到 AssemblyMateControllerState 的状态补丁。
 */
export function resolveAssemblyMateConcentricMisalignmentStatePatch(input: {
  /** 当前配合类型。 */
  readonly mateType: AssemblyMateType | null;
  /** 当前 PropertyManager 配合候选。 */
  readonly candidates: readonly AssemblyMateSelectionCandidate[];
  /** 当前 Mate 文档内的 Mate 列表。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 当前控制器状态。 */
  readonly state: AssemblyMateControllerState;
}): Pick<
  AssemblyMateControllerState,
  | 'concentricMisalignmentEnabled'
  | 'concentricMisalignmentType'
  | 'concentricMisalignmentLinkedMateId'
  | 'concentricMisalignmentMaximumDeviation'
  | 'concentricMisalignmentLinkedMateOptions'
> {
  const currentMateId = input.state.editingMateId ?? input.state.draftMateId;
  const linkedMateOptions = resolveLinkedConcentricMateOptions({
    mateType: input.mateType,
    candidates: input.candidates,
    mateDefinitions: input.mateDefinitions,
    currentMateId,
  });
  const linkedMateIds = new Set(linkedMateOptions.map((option) => option.mateId));
  const linkedMateId = input.state.concentricMisalignmentLinkedMateId
    && linkedMateIds.has(input.state.concentricMisalignmentLinkedMateId)
    ? input.state.concentricMisalignmentLinkedMateId
    : linkedMateOptions[0]?.mateId ?? null;
  const enabled = input.mateType === AssemblyMateType.Concentric
    && linkedMateOptions.length > 0
    && input.state.concentricMisalignmentEnabled;

  return {
    concentricMisalignmentEnabled: enabled,
    concentricMisalignmentType: input.state.concentricMisalignmentType,
    concentricMisalignmentLinkedMateId: linkedMateId,
    concentricMisalignmentMaximumDeviation: resolveMaximumDeviation(
      input.state.concentricMisalignmentMaximumDeviation,
    ),
    concentricMisalignmentLinkedMateOptions: linkedMateOptions,
  };
}
